import { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  FormLabel,
  Button,
  Autocomplete,
  FormControl,
  Paper,
  Typography,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { AttachFile, Delete, Add, Star, StarBorder } from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
  normalizeMultilineText,
} from "../../Components/helpers/Helpers";
import api from "../../api/axios";
import { useNavigate } from "react-router-dom";

const SI_NO_OPTIONS = [
  { nombre: "Seleccionar", id: -1 },
  { nombre: "Si", id: 1 },
  { nombre: "No", id: 0 },
];

const Crear = () => {
  const navigate = useNavigate();
  const { goPanel, goTable, buildTableUrl } = useLocationHelpers();
  const fileInputRef = useRef(null);

  const [agreementFiles, setAgreementFiles] = useState([]); // File[]
  const [agreementFilesDynamicNames, setAgreementFilesDynamicNames] = useState(
    [],
  );
  const [principalDocIndex, setPrincipalDocIndex] = useState(0); // index in agreementFiles
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [marketingToSend, setMarketingToSend] = useState(0);
  const [associationObservations, setAssociationObservations] = useState("");

  const [options, setOptions] = useState({
    companies: [],
    provinces: [],
    groups: [],
    notifications: SI_NO_OPTIONS,
    agreementSigned: SI_NO_OPTIONS,
  });

  const [associationData, setAssociationData] = useState({
    company_id: null,
    asoc_name: "",
    asoc_grupo: null,
    asoc_notifications: null,
    asoc_contactname: "",
    asoc_cif: "",
    asoc_address: "",
    asoc_province: null,
    asoc_postalcode: "",
    asoc_email: "",
    asoc_acuerdo: null,
    asoc_phone: "",
  });

  const getData = async () => {
    try {
      setLoadingOptions(true);
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/create`,
      );
      setOptions({
        companies: [
          { company_name: "Seleccionar", id: 0 },
          ...(data.companies ?? []),
        ],
        provinces: [
          { provincia: "Seleccionar", id: 0 },
          ...(data.provinces ?? []),
        ],
        groups: [{ grupo: "Seleccionar", id: 0 }, ...(data.groups ?? [])],
        notifications: SI_NO_OPTIONS,
        agreementSigned: SI_NO_OPTIONS,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los datos",
        confirmButtonColor: "#1ab394",
      });
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    getData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = ({ target }) => {
    let { name, value } = target;
    if (name === "asoc_phone") value = value.replace(/[^0-9]/g, "");
    if (name === "asoc_cif") value = value.replace(/[^a-zA-Z0-9]/g, "");
    if (name === "asoc_postalcode")
      value = value.replace(/[^0-9]/g, "").slice(0, 10);
    setAssociationData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAutocompleteChange = (_, value, fieldName) => {
    setAssociationData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const getAutocompleteOptionLabel = (option) =>
    option?.grupo ??
    option?.company_name ??
    option?.provincia ??
    option?.nombre ??
    "";

  const renderAutocompleteOption = (props, option) => {
    const { key, ...rest } = props;
    const label = getAutocompleteOptionLabel(option);
    const uniqueKey = `${option?.id ?? "no-id"}-${label}`;

    return (
      <li {...rest} key={uniqueKey}>
        {label}
      </li>
    );
  };

  const handleAddFiles = (e) => {
    const existingNames = agreementFiles.map((f) => f.name);
    const incoming = Array.from(e.target.files ?? []).filter((f) => {
      if (f.type !== "application/pdf") return false;
      if (existingNames.includes(f.name)) return false; // no duplicates
      return true;
    });
    if (incoming.length < Array.from(e.target.files ?? []).length) {
      Swal.fire({
        icon: "warning",
        title: "Archivos duplicados",
        text: "Algunos archivos ya estaban en la lista o no son PDF y fueron omitidos.",
        confirmButtonColor: "#1ab394",
        timer: 2500,
        showConfirmButton: false,
      });
    }
    setAgreementFiles((prev) => [...prev, ...incoming]);
    setAgreementFilesDynamicNames((prev) => [
      ...prev,
      ...incoming.map(() => ""),
    ]);
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setAgreementFiles((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // adjust principal index
      if (principalDocIndex === index) {
        setPrincipalDocIndex(0);
      } else if (principalDocIndex > index) {
        setPrincipalDocIndex((p) => p - 1);
      }
      return updated;
    });
    setAgreementFilesDynamicNames((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChangeDynamicName = (index, value) => {
    setAgreementFilesDynamicNames((prev) =>
      prev.map((name, i) => (i === index ? value : name)),
    );
  };

  const formFields = [
    {
      label: "Empresa",
      name: "company_id",
      type: "autocomplete",
      options: options.companies,
      getOptionLabel: (o) => o?.company_name || "",
      isOptionEqualToValue: (o, v) => o.id === v?.id,
    },
    {
      label: "Provincia",
      name: "asoc_province",
      type: "autocomplete",
      options: options.provinces,
      getOptionLabel: (o) => o?.provincia || "",
      isOptionEqualToValue: (o, v) => o.id === v?.id,
    },
    {
      label: "Grupos",
      name: "asoc_grupo",
      type: "autocomplete",
      options: options.groups,
      getOptionLabel: (o) => o?.grupo || "",
      isOptionEqualToValue: (o, v) => o.id === v?.id,
    },
    {
      label: "Notificaciones",
      name: "asoc_notifications",
      type: "autocomplete",
      options: options.notifications,
      getOptionLabel: (o) => o?.nombre || "",
      isOptionEqualToValue: (o, v) => o.id === v?.id,
    },
    {
      label: "Acuerdo firmado",
      name: "asoc_acuerdo",
      type: "autocomplete",
      options: options.agreementSigned,
      getOptionLabel: (o) => o?.nombre || "",
      isOptionEqualToValue: (o, v) => o.id === v?.id,
    },
    { name: "asoc_name", label: "Nombre", type: "text", length: 255 },
    { name: "asoc_phone", label: "Telefono", type: "text", length: 25 },
    {
      name: "asoc_contactname",
      label: "Persona de contacto",
      type: "text",
      length: 255,
    },
    { name: "asoc_cif", label: "CIF", type: "text", length: 50 },
    { name: "asoc_address", label: "Direccion", type: "text", length: 255 },
    {
      name: "asoc_postalcode",
      label: "Codigo postal",
      type: "text",
      length: 10,
      inputMode: "numeric",
    },
    { name: "asoc_email", label: "Email", type: "text", length: 255 },
    {
      name: "asoc_observaciones",
      label: "Observaciones",
      type: "textarea",
      required: false,
      length: 16777215,
    },
  ];

  const fieldLabelMap = Object.fromEntries(
    formFields.map((f) => [f.name, f.label]),
  );
  const requiredFields = formFields
    .filter((f) => f.required !== false)
    .map((f) => f.name);

  const isFilled = (val) => {
    if (val === null || val === undefined || val === "") return false;
    if (typeof val === "object" && "id" in val)
      return val.id !== 0 && val.id !== -1;
    return true;
  };

  const validateFields = () =>
    requiredFields.every((f) => isFilled(associationData[f]));

  const showValidationErrors = () => {
    const missing = requiredFields
      .filter((f) => !isFilled(associationData[f]))
      .map((f) => `<li>${fieldLabelMap[f]}</li>`)
      .join("");
    Swal.fire({
      icon: "error",
      title: "Campos obligatorios",
      html: `<div style="text-align:left">Complete los siguientes campos:<ul>${missing}</ul></div>`,
      confirmButtonColor: "#1ab394",
    });
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const buildFormData = () => {
    const fd = new FormData();
    Object.entries(associationData).forEach(([key, value]) => {
      if (value && typeof value === "object") {
        if (key === "asoc_province") {
          fd.append(key, value.provincia ?? "");
        } else {
          fd.append(key, value.id ?? "");
        }
      } else {
        fd.append(key, value ?? "");
      }
    });
    fd.append(
      "asoc_observaciones",
      normalizeMultilineText(associationObservations),
    );
    fd.append("marketing", marketingToSend ? 1 : 0);
    agreementFiles.forEach((file) => {
      fd.append("agreement_files[]", file);
    });

    const filesMeta = agreementFiles.map((file, index) => ({
      index,
      original: file.name,
      nombre_dinamico: (agreementFilesDynamicNames[index] ?? "").trim() || null,
    }));
    fd.append("agreement_files_meta", JSON.stringify(filesMeta));

    // Tell backend which index is the principal doc
    fd.append("principal_doc_index", principalDocIndex);
    return fd;
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      showValidationErrors();
      return;
    }
    if (
      associationData.asoc_email &&
      !isValidEmail(associationData.asoc_email)
    ) {
      Swal.fire({
        icon: "error",
        title: "Email invalido",
        text: "Por favor ingrese un correo electronico valido",
        confirmButtonColor: "#1ab394",
      });
      return;
    }

    const confirm = await Swal.fire({
      title: "Desea guardar los cambios?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1ab394",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;

    Swal.fire({
      title: "Guardando asociacion...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/store`,
        buildFormData(),
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      Swal.close();

      const associationId = data?.association_created?.id;
      const success = await Swal.fire({
        icon: "success",
        title: "Asociacion creada",
        confirmButtonText: "Ver asociacion",
        confirmButtonColor: "#1ab394",
        allowEscapeKey: false,
        allowOutsideClick: false,
      });
      if (success.isConfirmed && associationId) {
        navigate(`/asociaciones/ver?id=${associationId}`);
      }
    } catch {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo crear la asociacion",
        confirmButtonColor: "#1ab394",
      });
    }
  };

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Asociaciones" subTitle="Crear" />

      <Box
        component="form"
        noValidate
        sx={{
          maxWidth: 1100,
          mx: "auto",
          mt: { xs: 1, md: 2 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Nueva asociacion
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            {formFields.map((field, idx) =>
              field.type === "autocomplete" ? (
                <Grid item xs={12} sm={6} key={idx}>
                  <Autocomplete
                    value={associationData[field.name]}
                    options={field.options}
                    loading={loadingOptions}
                    size="small"
                    getOptionLabel={field.getOptionLabel}
                    isOptionEqualToValue={field.isOptionEqualToValue}
                    renderOption={renderAutocompleteOption}
                    onChange={(e, v) =>
                      handleAutocompleteChange(e, v, field.name)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={field.label} fullWidth />
                    )}
                  />
                </Grid>
              ) : field.type === "textarea" ? (
                <Grid item xs={12} key={idx}>
                  <FormControl fullWidth>
                    <FormLabel sx={{ mb: 0.5, fontSize: 13 }}>
                      {field.label}
                    </FormLabel>
                    <textarea
                      maxLength={field.length}
                      style={{
                        height: 100,
                        resize: "vertical",
                        padding: "8px 12px",
                        borderRadius: 4,
                        border: "1px solid rgba(0,0,0,0.23)",
                        fontSize: 14,
                        fontFamily: "inherit",
                      }}
                      onChange={(e) =>
                        setAssociationObservations(e.target.value)
                      }
                    />
                  </FormControl>
                </Grid>
              ) : (
                <Grid item xs={12} sm={6} key={idx}>
                  <TextField
                    name={field.name}
                    label={field.label}
                    value={associationData[field.name]}
                    inputProps={
                      field.length
                        ? {
                            maxLength: field.length,
                            inputMode: field.inputMode,
                          }
                        : undefined
                    }
                    onChange={handleChange}
                    size="small"
                    fullWidth
                  />
                </Grid>
              ),
            )}

            {/* Marketing */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Checkbox
                  checked={marketingToSend === 1}
                  onChange={() => setMarketingToSend((p) => (p ? 0 : 1))}
                  color="success"
                  sx={{ mt: "2px", p: 0 }}
                />
                <Typography fontSize={14}>
                  Acepto el uso de mi correo electronico para el envio de
                  comunicaciones informativas y comerciales.
                </Typography>
              </Paper>
            </Grid>

            {/* Documentos */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{ p: 2, borderStyle: "dashed", borderRadius: 2 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      Documentos adjuntos (PDF)
                    </Typography>
                    {agreementFiles.length > 0 && (
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      >
                        Pulsa la estrella para marcar el documento principal
                      </Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Anadir
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleAddFiles}
                  />
                </Box>

                {agreementFiles.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    Sin documentos. Pulsa "Anadir" para adjuntar PDF(s).
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {agreementFiles.map((file, i) => {
                      const isPrincipal = i === principalDocIndex;
                      return (
                        <ListItem
                          key={i}
                          disablePadding
                          sx={{
                            py: 0.5,
                            px: 1,
                            borderRadius: 1,
                            border: isPrincipal
                              ? "1px solid #f5a623"
                              : "1px solid transparent",
                            bgcolor: isPrincipal ? "#fffbf0" : "transparent",
                            mb: 0.5,
                            "&:hover": {
                              bgcolor: isPrincipal ? "#fffbf0" : "action.hover",
                            },
                          }}
                        >
                          <Tooltip
                            title={
                              isPrincipal
                                ? "Documento principal"
                                : "Establecer como principal"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => setPrincipalDocIndex(i)}
                              sx={{ mr: 0.5 }}
                            >
                              {isPrincipal ? (
                                <Star sx={{ color: "#f5a623", fontSize: 18 }} />
                              ) : (
                                <StarBorder
                                  sx={{ color: "#bbb", fontSize: 18 }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                          <AttachFile
                            sx={{ fontSize: 16, color: "primary.main", mr: 1 }}
                          />
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <span style={{ fontSize: 13 }}>
                                  {file.name}
                                </span>
                                {isPrincipal && (
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: "#f5a623",
                                      bgcolor: "#fff3cc",
                                      px: 0.8,
                                      borderRadius: 1,
                                    }}
                                  >
                                    PRINCIPAL
                                  </Typography>
                                )}
                                <TextField
                                  size="small"
                                  placeholder="Nombre dinamico (opcional)"
                                  value={agreementFilesDynamicNames[i] ?? ""}
                                  onChange={(e) =>
                                    handleChangeDynamicName(i, e.target.value)
                                  }
                                  sx={{ minWidth: 220 }}
                                />
                              </Box>
                            }
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveFile(i)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ my: 3 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                >
                  Guardar
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <NavButton
                  fullWidth
                  variant="outlined"
                  color="error"
                  to={buildTableUrl("asociaciones")}
                  onClick={() => goTable("asociaciones")}
                >
                  Tabla
                </NavButton>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button fullWidth variant="outlined" onClick={goPanel}>
                  Inicio
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default Crear;
